import { call } from '@advanza/api'
import { Col, Row } from '@advanza/grid'
import { InputField } from '@advanza/input'
import { Button, Divider, Icon, LoadingDots } from '@advanza/ui'
import { fetchProvider } from 'actions/providers'
import clsx from 'clsx'
import AdminUser from 'components/adminUsers/AdminUser'
import CheckButton from 'components/CheckButton'
import AutoRow from 'components/misc/AutoRow'
import AddLeadBoosterPromoLeadsButton from 'components/providers/AddLeadBoosterPromoLeadsButton'
import Restricted from 'components/Restricted'
import FormattedTime from 'components/ui/FormattedTime'
import SafeButton from 'components/ui/SafeButton'
import { format } from 'date-fns'
import { useAppDispatch } from 'hooks/hooks'
import { useProvider } from 'hooks/providerHooks'
import { useEffect, useState } from 'react'
import DatePicker from '../DatePicker'
import style from './LeadBoostersOffice.module.css'

interface LeadBooster {
    lead_booster_id: number
    service_provider_id?: number
    date_start: string
    date_end?: string
    booster_factor: string
}

interface NewLeadBooster {
    service_provider_id?: number
    date_start?: string
    date_end?: string
    booster_factor?: number
}

const DEFAULT_BOOSTER_FACTOR = 1.5

interface LeadBoosterRowProps {
    booster: LeadBooster
}

const LeadBoosterRow = ({ booster }: LeadBoosterRowProps) => {
    const { deleteBooster } = useLeadBoosters(booster.service_provider_id)

    const format = 'd MMM yyyy'
    return (
        <div key={booster.lead_booster_id} className={style.newBooster}>
            <small>#{booster.lead_booster_id}</small>
            <div>
                <FormattedTime format={format} date={booster.date_start} />
            </div>
            <span>→</span>
            <div>
                {booster.date_end ? <FormattedTime format={format} date={booster.date_end} /> : '∞'}
            </div>
            <div className="bg-grey-500 text-bodyDark text-md py-0.5 rounded p-1">
                ⚡{booster.booster_factor}×{' '}
                {Number(booster.booster_factor) === DEFAULT_BOOSTER_FACTOR && '(default)'}
            </div>
            <SafeButton
                buttonText={<Icon name="delete_forever" />}
                action={() => deleteBooster(booster.lead_booster_id)}
                successMessage="Deleted booster"
            />
        </div>
    )
}

const useLeadBoosters = (providerId = 0) => {
    const [isLoading, setIsLoading] = useState<boolean>(false)
    const [isSaving, setIsSaving] = useState<boolean>(false)
    const [leadBoostersScheduled, setLeadBoostersScheduled] = useState<LeadBooster[]>([])
    const [leadBoostersActive, setLeadBoostersActive] = useState<LeadBooster[]>([])
    const [leadBoostersPast, setLeadBoostersPast] = useState<LeadBooster[]>([])
    const [newBooster, setNewBooster] = useState<NewLeadBooster | null>(null)
    const [force, setForce] = useState<boolean>(false)
    const dispatch = useAppDispatch()

    const fetchLeadBoosters = async () => {
        setIsLoading(true)
        const result: { scheduled: LeadBooster[]; active: LeadBooster[]; past: LeadBooster[] } =
            await call(`office/lead-boosters/list/${providerId}`)
        setLeadBoostersActive(result.active)
        setLeadBoostersScheduled(result.scheduled)
        setLeadBoostersPast(result.past)
        setIsLoading(false)
    }
    const saveBooster = async () => {
        setIsSaving(true)
        try {
            await call(`office/lead-boosters/save/${force ? 1 : 0}`, {
                json: {
                    booster_factor: newBooster?.booster_factor ?? DEFAULT_BOOSTER_FACTOR,
                    ...newBooster,
                    service_provider_id: providerId,
                },
            })
            setIsSaving(false)
            await fetchLeadBoosters()
            await dispatch(fetchProvider(providerId, true))
            setNewBooster(null)
        } catch (e) {
            alert(JSON.stringify(e, null, 2))
            setIsSaving(false)
        }
    }
    const changeBooster = (changes: NewLeadBooster) => {
        setNewBooster({ ...(newBooster ?? { booster_factor: DEFAULT_BOOSTER_FACTOR }), ...changes })
    }
    const removeNewBooster = () => setNewBooster(null)
    const deleteBooster = async (id: number) => {
        setIsSaving(true)
        await call(`office/lead-boosters/delete/${id}`, { method: 'delete' })
        setIsSaving(false)
        await fetchLeadBoosters()
        await dispatch(fetchProvider(providerId, true))
    }

    const deactivatePromoLead = async (id: number) => {
        setIsSaving(true)
        await call(`office/providers/deactivate-lead-booster-promo-lead/${id}`, { method: 'post' })
        setIsSaving(false)
        dispatch(fetchProvider(providerId, true))
    }

    useEffect(() => {
        fetchLeadBoosters()
    }, [])

    return {
        leadBoostersScheduled,
        leadBoostersActive,
        leadBoostersPast,
        saveBooster,
        deleteBooster,
        deactivatePromoLead,
        changeBooster,
        newBooster,
        force,
        setForce,
        removeNewBooster,
        isLoading,
        isSaving,
    }
}

const LeadBoostersOffice = ({ providerId }: Props) => {
    const {
        leadBoostersActive,
        leadBoostersScheduled,
        leadBoostersPast,
        saveBooster,
        isSaving,
        changeBooster,
        deactivatePromoLead,
        isLoading,
        newBooster,
        force,
        setForce,
        removeNewBooster,
    } = useLeadBoosters(providerId)

    const { provider } = useProvider(providerId)

    const hasNew = newBooster !== null
    const hasScheduledBoosters = leadBoostersScheduled.length > 0
    const hasActiveLeadBoosters = leadBoostersActive.length > 0
    const hasPastLeadBoosters = leadBoostersPast.length > 0
    return (
        <div className={isLoading || isSaving ? style.loading : ''}>
            {hasScheduledBoosters && (
                <>
                    <h4>Scheduled</h4>
                    {leadBoostersScheduled.map((booster) => (
                        <LeadBoosterRow key={booster.lead_booster_id} booster={booster} />
                    ))}
                </>
            )}

            {hasActiveLeadBoosters && (
                <>
                    <h4>Active</h4>
                    {leadBoostersActive.map((booster) => (
                        <LeadBoosterRow key={booster.lead_booster_id} booster={booster} />
                    ))}
                </>
            )}

            {hasPastLeadBoosters && (
                <>
                    <h4>Past</h4>
                    {leadBoostersPast.map((booster) => (
                        <LeadBoosterRow key={booster.lead_booster_id} booster={booster} />
                    ))}
                </>
            )}

            {provider && provider.lead_booster_promo_leads.length > 0 && (
                <>
                    <h4 className={'my-2'}>Promo leads</h4>
                    {provider?.lead_booster_promo_leads.map(
                        ({
                            lead_booster_promo_lead_id,
                            nr_promo_leads,
                            nr_promo_leads_left,
                            user_id,
                            comment,
                            created,
                            active,
                        }) => {
                            return (
                                <Row
                                    key={lead_booster_promo_lead_id}
                                    between="xs"
                                    className={clsx(!active && style.inActive)}>
                                    <Col xs>
                                        <b>
                                            {nr_promo_leads_left}/{nr_promo_leads}
                                        </b>{' '}
                                        leads left from <AdminUser userId={user_id} />{' '}
                                        <small>
                                            <FormattedTime format="yyyy-MM-dd" date={created} />
                                            {comment}
                                        </small>
                                    </Col>
                                    <Col x>
                                        <SafeButton
                                            buttonText={<Icon name="undo" />}
                                            action={() =>
                                                deactivatePromoLead(lead_booster_promo_lead_id)
                                            }
                                            confirmMessage="deactivate promo lead"
                                            cancelText={false}
                                        />
                                    </Col>
                                </Row>
                            )
                        },
                    )}
                </>
            )}

            <Divider m />
            {hasNew && (
                <div>
                    <AutoRow noWrap>
                        <DatePicker
                            onChange={(e) => changeBooster({ date_start: e.target.value })}
                            value={newBooster.date_start}
                            name="Date start"
                        />
                        <DatePicker
                            onChange={(e) => changeBooster({ date_end: e.target.value })}
                            value={newBooster.date_end}
                            name="Date end"
                        />
                        <div
                            title="Use a custom booster factor. E.g.: '2.0' -> increases price and ranking by 2x. Default = 1.5"
                            className="w-full max-w-40">
                            <InputField
                                name="booster_factor"
                                type="number"
                                placeholder="⚡ Booster factor"
                                step="0.1"
                                min="0.1"
                                max="10"
                                value={newBooster.booster_factor ?? DEFAULT_BOOSTER_FACTOR}
                                onChange={(e) =>
                                    changeBooster({
                                        booster_factor: e.target.value
                                            ? parseFloat(e.target.value)
                                            : DEFAULT_BOOSTER_FACTOR,
                                    })
                                }
                                after={
                                    <Button
                                        appearance="text_primary"
                                        title="Reset booster factor"
                                        disabled={
                                            newBooster.booster_factor === DEFAULT_BOOSTER_FACTOR
                                        }
                                        onClick={() => {
                                            changeBooster({
                                                booster_factor: DEFAULT_BOOSTER_FACTOR,
                                            })
                                        }}>
                                        ⌫
                                    </Button>
                                }
                            />
                        </div>

                        <CheckButton
                            msg="Lifetime"
                            checked={!newBooster.date_end}
                            onChange={() =>
                                changeBooster({
                                    date_end: newBooster.date_end
                                        ? undefined
                                        : format(new Date(), 'yyyy-MM-dd'),
                                })
                            }
                        />
                        <Restricted location="admin">
                            <CheckButton
                                msg="Force"
                                checked={force}
                                onChange={() => setForce((force) => !force)}
                            />
                        </Restricted>
                    </AutoRow>
                    <div className="mt-3 text-md italic leading-relaxed">
                        The booster factor will also increase the price with that rate. Example:
                        booster of 1.5, will increase the price with 1.5x. Booster of 2.5, will
                        increase the price with 2.5x. Etc.{' '}
                        <span className="not-italic font-medium text-blue-900">
                            The standard lead booster, will have a factor of 1.5.
                        </span>
                    </div>
                    <Divider m />

                    <AutoRow>
                        <Button name="default" onClick={saveBooster}>
                            {isSaving ? <LoadingDots color="#fff" /> : 'Save booster'}
                        </Button>
                        <Button name="text" onClick={removeNewBooster}>
                            <Icon name="close" />
                        </Button>
                    </AutoRow>
                </div>
            )}
            {!hasNew && (
                <div className="flex flex-col items-start gap-1">
                    <Button
                        name="text"
                        onClick={() =>
                            changeBooster({ date_start: format(new Date(), 'yyyy-MM-dd') })
                        }>
                        Add booster
                    </Button>
                    <AddLeadBoosterPromoLeadsButton providerId={providerId} />
                </div>
            )}
        </div>
    )
}

interface Props {
    providerId: number
}

export default LeadBoostersOffice
